import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useGetOrganisationClientCredentials } from '../../../../../app/ApiGen';
import { useCurrentOrganisation } from '../../../../../app/CurrentOrganisationProvider';
import { DataErrorHandler } from '../../../../../components/ErrorHandler';
import { Card, Loading } from '../../../../../design-system';
import type { SubState } from '../../../../../store/types';

const FetchContext: React.FC<{ organisation: SubState<'organisation'> }> = ({ organisation }) => {
  const { data, error, loading, refetch } = useGetOrganisationClientCredentials({
    orgSlug: organisation.slug,
  });
  const [showClientsDetails, setShowClientsDetails] = React.useState(false);
  const { push } = useHistory();

  if (loading) {
    return <Loading />;
  }

  const clientCredentials = data ? data.data : { id: '****', secret: '****' };
  const apiAccess = !!organisation.tierDetails.apiAccess || !!organisation.tierDetails.demandResponse;

  const handleClickShowClientsDetails = () => {
    setShowClientsDetails(!showClientsDetails);
  };

  if (error) {
    return error.status === 403 ? null : (
      <DataErrorHandler
        description="Unable to load API client credentials"
        error={error}
        refetch={refetch}
        type="embedded"
      />
    );
  }

  return (
    <>
      <Card>
        <Box>
          <Grid container justifyContent="space-between" spacing={2}>
            <Grid xs={8}>
              <Typography style={{ padding: '16px' }} color="textSecondary" variant="body2" paragraph>
                API details
              </Typography>
            </Grid>
            <Grid
              style={{ padding: '16px', marginRight: '8px', marginTop: '4px' }}
              container
              direction="column"
              xs={3}
              alignContent="flex-end"
            >
              {apiAccess ? (
                <Button
                  color="inherit"
                  endIcon={showClientsDetails ? <Visibility /> : <VisibilityOff />}
                  onClick={handleClickShowClientsDetails}
                  type="button"
                  variant="outlined"
                >
                  {showClientsDetails ? 'Hide' : 'Show'} API details
                </Button>
              ) : (
                <Tooltip title="Upgrade to unlock API access.">
                  <Button
                    color="primary"
                    endIcon={<LockOutlinedIcon />}
                    onClick={() => push(`/organisations/${organisation?.slug}/upgrade`)}
                    variant="contained"
                  >
                    Show API details
                  </Button>
                </Tooltip>
              )}
            </Grid>
          </Grid>

          <Box p={2} pt={0}>
            <Grid container>
              <Grid xs={4}>
                <Typography color="textSecondary" variant="body2" paragraph>
                  Client ID
                </Typography>
              </Grid>
              <Grid xs={8}>
                <Typography variant="body2" paragraph style={{ marginLeft: '-20px' }}>
                  {clientCredentials.id}
                </Typography>
              </Grid>
            </Grid>
            <Grid container>
              <Grid xs={4}>
                <Typography color="textSecondary" variant="body2" paragraph>
                  Client Secret
                </Typography>
              </Grid>
              <Grid xs={8}>
                <Typography variant="body2" paragraph style={{ marginLeft: '-20px' }}>
                  {showClientsDetails ? clientCredentials.secret : '****'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>
    </>
  );
};

export const APIDetails: React.FC = () => {
  const { organisation, loading } = useCurrentOrganisation();

  if (loading || !organisation) {
    return <Loading />;
  }
  return <FetchContext organisation={organisation} />;
};
